import { useState } from 'react';
import classNames from 'classnames';
import { Metadata, MetadataProps } from './Metadata';
import './MetadataGroup.scss';

export interface IntlRatingProps {
  /** optional img source to display the international rating icon */
  intlRatingSrc?: string;
  /** alt text for intlRating image */
  intlRatingAlt?: string;
}

export interface MetadataGroupProps {
  /** array of Metadata props and international rating image props */
  metadataItems?: Array<MetadataProps | IntlRatingProps>;
  /** optional size prop to pass to Metadata, default large */
  size?: 'small' | 'medium' | 'large';
  /** option to pass in Metadata directly instead of generating through metadataItems */
  children?: React.ReactNode;
  /** additional class names */
  className?: string;
  /** when true, render metadataItems in caller order; default false preserves image-first grouping */
  preserveItemOrder?: boolean;
}

/**
 * MetadataGroup - renders a list of Metadata, either through metadataItems or children.
 * Also supports international rating image.
 * @param metadataItems - array of Metadata props and international rating image props
 * @param size - optional size prop to pass to Metadata, default large
 * @param children - option to pass in Metadata directly instead of generating through metadataItems
 * @param className - additional class names
 * @param preserveItemOrder - when true, render metadataItems in caller order; default false preserves image-first grouping
 */
export const MetadataGroup: React.FC<MetadataGroupProps> = ({
  metadataItems,
  size = 'large',
  children,
  className,
  preserveItemOrder = false,
}) => {
  const [hasError, setHasError] = useState<boolean>(false);

  // Shared across all intl rating images in this group; one failed load hides every rating icon.
  const renderIntlRating = (item: IntlRatingProps, index: number) =>
    !hasError ? (
      <img
        key={`intl-rating-${index}`}
        className="vds-metadata-group__intl-rating"
        src={item.intlRatingSrc}
        alt={item.intlRatingAlt ?? ''}
        onError={() => setHasError(true)}
      />
    ) : null;

  const renderMetadata = (item: MetadataProps, index: number) => (
    <Metadata key={`metadata-${index}`} {...item} size={size} />
  );

  const renderMetadataItems = () => {
    if (!metadataItems) {
      return null;
    }

    // Ordered mode: render each item in caller sequence (e.g. Genre → rating icon → Runtime → Year).
    // A single metadataItems entry may include both intlRatingSrc and children; in that case the
    // rating image is rendered immediately before the text node for that entry.
    if (preserveItemOrder) {
      return metadataItems.reduce<React.ReactNode[]>((nodes, item, index) => {
        if ('intlRatingSrc' in item && item.intlRatingSrc) {
          const intlRating = renderIntlRating(item, index);
          if (intlRating) {
            nodes.push(intlRating);
          }
        }

        if ('children' in item && item.children) {
          nodes.push(renderMetadata(item, index));
        }

        return nodes;
      }, []);
    }

    // Default mode: partition items by shape, then render all rating images before all text metadata.
    // This preserves legacy behavior for consumers that relied on image-first grouping.
    const intlRatingItems = metadataItems.filter(
      (item): item is IntlRatingProps => 'intlRatingSrc' in item && !!item.intlRatingSrc,
    );
    const metadataChildrenItems = metadataItems.filter(
      (item): item is MetadataProps => 'children' in item && !!item.children,
    );

    return (
      <>
        {intlRatingItems.map((item, index) => renderIntlRating(item, index))}
        {metadataChildrenItems.map((item, index) => renderMetadata(item, index))}
      </>
    );
  };

  return (
    <div className={classNames('vds-metadata-group', `vds-metadata-group--${size}`, className)}>
      {renderMetadataItems()}
      {children}
    </div>
  );
};
